import { reactive } from 'vue';

export enum ScreenWidth {
    min = 'min',
    dXs = 'dXs',
}

// WARNING: order must be ascending
export const breakpoints = {
    [ScreenWidth.min]: 0,
    [ScreenWidth.dXs]: 1279,
};

// `window` is guarded so an async dynamic import (e.g. modal registry chunks)
// resolving after test teardown doesn't throw — vitest reports this as an
// "Unhandled Rejection" that pollutes runs.
export const screenInfo = reactive({
    width: typeof window === 'undefined' ? 0 : window.innerWidth,
    height: typeof window === 'undefined' ? 0 : window.innerHeight,
});

// The function name isincorrect.The more precise name would be not desktop.
export function isMobileLayout(): boolean {
    return respondToBelow(ScreenWidth.dXs);
}

export function respondToBelow(maxWidth: ScreenWidth): boolean {
    return respondToBelowWidth(screenInfo.width, maxWidth);
}

export function respondToBelowWidth(width: number, maxWidth: ScreenWidth): boolean {
    return breakpoints[maxWidth] >= width;
}

export function attachResizeListener(): void {
    window.addEventListener(
        'resize',
        throttle(
            () => {
                const height = window.innerHeight;
                screenInfo.width = window.innerWidth;
                screenInfo.height = height;
            },
            200,
            { leading: true, trailing: true },
        ),
    );
}

/**
 * Creates a throttled function that only invokes func at most once per every wait milliseconds.
 * Provide options to indicate whether func should be invoked on the leading and/or trailing
 * edge of the wait timeout.
 * If leading and trailing options are true, func is invoked on the trailing edge of the timeout
 * only if the throttled function is invoked more than once during the wait timeout.
 */
export function throttle<Args extends unknown[], R>(
    func: (...args: Args) => R,
    wait: number,
    {
        leading = true,
        trailing = true,
    }: {
        leading?: boolean;
        trailing?: boolean;
    } = {},
): (...args: Args) => R | undefined {
    let shouldThrottle = false;
    let isThrottled = false;
    let result: R | undefined;
    return (...args: Args): R | undefined => {
        if (shouldThrottle) {
            isThrottled = true;
            return result;
        }
        shouldThrottle = true;
        if (leading) {
            result = func(...args);
        }
        setTimeout(() => {
            if ((trailing && !leading) || (trailing && isThrottled)) {
                result = func(...args);
            }
            shouldThrottle = false;
            isThrottled = false;
        }, wait);
        return result;
    };
}
